import { Extension, type Editor } from "@tiptap/core";
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state";

export const slashCommands = [
  { id: "heading1", group: 0, search: "heading 1 h1 제목 헤딩 1" },
  { id: "heading2", group: 0, search: "heading 2 h2 제목 헤딩 2" },
  { id: "heading3", group: 0, search: "heading 3 h3 제목 헤딩 3" },
  { id: "heading4", group: 0, search: "heading 4 h4 제목 헤딩 4" },
  { id: "bulletList", group: 1, search: "bulleted list bullet unordered 블릿 불릿 글머리 기호 목록" },
  { id: "orderedList", group: 1, search: "numbered list ordered 넘버 번호 숫자 목록" },
  { id: "taskList", group: 1, search: "checklist task todo check 체크 할일 목록" },
  { id: "link", group: 2, search: "link reference doc task issue uid 링크 연결 문서 태스크 이슈" },
  { id: "media", group: 2, search: "insert media image video picture 미디어 이미지 사진 동영상 비디오" },
  { id: "file", group: 2, search: "attach files upload attachment 파일 첨부 업로드" },
  { id: "table", group: 3, search: "table grid 표 테이블" },
  { id: "codeBlock", group: 3, search: "code block 코드 블록 블락" },
  { id: "blockquote", group: 3, search: "blockquote quote 인용문 인용 블록 코호트" },
] as const;
export type SlashCommandId = typeof slashCommands[number]["id"];
export type SlashMatch = { from: number; to: number; query: string; index: number };
type SlashState = { active: SlashMatch | null; dismissed: number | null };
export const slashKey = new PluginKey<SlashState>("ternSlashCommands");

export function matchingCommands(query: string) {
  const words = query.toLowerCase().trim().split(/\s+/);
  return slashCommands.filter((command) => words.every((word) => command.search.includes(word)));
}

function matchSlash(state: EditorState): SlashMatch | null {
  const { $from, empty } = state.selection;
  if (!empty || !$from.parent.isTextblock || $from.parent.type.spec.code || $from.marks().some((mark) => ["code", "link"].includes(mark.type.name))) return null;
  const text = $from.parent.textBetween(0, $from.parentOffset, "\0", "\0");
  const match = /(?:^|\s)\/([^/\n]{0,40})$/.exec(text);
  if (!match) return null;
  return { from: $from.pos - match[1]!.length - 1, to: $from.pos, query: match[1]!, index: 0 };
}

export function dismissSlash(editor: Editor) {
  editor.view.dispatch(editor.state.tr.setMeta(slashKey, { close: true }));
}

/** Delete only the active slash query. Existing text and neighbouring blocks survive. */
export function applySlashCommand(editor: Editor, id: SlashCommandId, range: Pick<SlashMatch, "from" | "to">) {
  const chain = editor.chain().focus().deleteRange(range);
  switch (id) {
    case "heading1": return chain.clearNodes().setHeading({ level: 1 }).run();
    case "heading2": return chain.clearNodes().setHeading({ level: 2 }).run();
    case "heading3": return chain.clearNodes().setHeading({ level: 3 }).run();
    case "heading4": return chain.clearNodes().setHeading({ level: 4 }).run();
    case "bulletList": return chain.toggleBulletList().run();
    case "orderedList": return chain.toggleOrderedList().run();
    case "taskList": return chain.toggleTaskList().run();
    case "codeBlock": return chain.clearNodes().setCodeBlock().run();
    case "blockquote": return chain.setBlockquote().run();
    case "table": return chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    default: return false; // File commands wait for the picker and a successful upload.
  }
}

export const SlashCommands = Extension.create({
  name: "slashCommands",
  priority: 1000,
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [new Plugin<SlashState>({
      key: slashKey,
      state: {
        init: () => ({ active: null, dismissed: null }),
        apply(tr, prev, _old, state) {
          const meta = tr.getMeta(slashKey) as { close?: boolean; index?: number } | undefined;
          const match = matchSlash(state);
          if (meta?.close) return { active: null, dismissed: match?.from ?? null };
          if (!match) return { active: null, dismissed: null };
          const dismissed = prev.dismissed === null ? null : tr.mapping.map(prev.dismissed);
          if (dismissed === match.from) return { active: null, dismissed };
          const same = prev.active?.from === match.from && prev.active.query === match.query;
          match.index = Math.min(meta?.index ?? (same ? prev.active!.index : 0), Math.max(0, matchingCommands(match.query).length - 1));
          return { active: match, dismissed: null };
        },
      },
      props: {
        handleKeyDown(view, event) {
          const match = slashKey.getState(view.state)?.active;
          if (!editor.isEditable || !match || event.isComposing || view.composing || event.keyCode === 229 || event.metaKey || event.ctrlKey || event.altKey) return false;
          const commands = matchingCommands(match.query);
          if (!["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)) return false;
          if (!commands.length && event.key !== "Escape") return false;
          event.preventDefault();
          event.stopPropagation();
          if (event.key === "Escape") dismissSlash(editor);
          else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            const index = (match.index + (event.key === "ArrowDown" ? 1 : -1) + commands.length) % commands.length;
            view.dispatch(view.state.tr.setMeta(slashKey, { index }));
          } else {
            const id = commands[match.index]!.id;
            if (id === "link") view.dom.dispatchEvent(new CustomEvent("tern:link-command", { detail: { range: match } }));
            else if (id === "file" || id === "media") view.dom.dispatchEvent(new CustomEvent("tern:file-command", { detail: { id, range: match } }));
            else applySlashCommand(editor, id, match);
          }
          return true;
        },
      },
    })];
  },
});
